/**
 * =============================================================================
 * useAudit — admin audit-trail viewer data hook (PR I-2)
 * =============================================================================
 *
 * Read-only hook over `adminApi.audit.list` (the ADR-023 display-safe read
 * surface). Owns the loaded rows + keyset cursor and exposes two actions:
 *
 *   - `applyFilters(filters)` — (re)load the FIRST page with the given filters,
 *     REPLACING the loaded rows. Called on first expand and whenever filters
 *     change.
 *   - `loadOlder()`            — load the next (older) keyset page via
 *     `nextCursor` and APPEND it. No-op when there is no further page.
 *
 * Every request runs under its own `AbortController`; a newer request supersedes
 * (aborts) any in-flight one, and a caller abort is silently ignored (never
 * surfaced as an error) — enabled by the PR I-2 `fetchWithRetry`/`apiRequest`
 * caller-abort propagation. State is guarded against unmount and against a stale
 * (superseded) response overwriting a newer one.
 *
 * This hook performs NO fetch on mount — it is lazy; the panel drives the first
 * load so the audit endpoint isn't hit on every dashboard render.
 * =============================================================================
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuditLogListQuery, AuditLogView } from '@find-my-part/types';
import { adminApi } from '../api/client';

/** Page size for the audit viewer (backend clamps to [1, 100]). */
export const AUDIT_PAGE_LIMIT = 20;

export interface UseAuditResult {
  /** Loaded audit rows, newest-first, growing as older pages are appended. */
  logs: AuditLogView[];
  /** Whether a further (older) page exists (`nextCursor !== null`). */
  hasMore: boolean;
  /** Initial load / filter-change load in flight. */
  isLoading: boolean;
  /** "Load older" append in flight. */
  isLoadingMore: boolean;
  /** User-facing error message, or null. Never set for a caller-aborted request. */
  error: string | null;
  /** True once the first successful load has completed (drives lazy UI). */
  hasLoaded: boolean;
  /** (Re)load the first page with `filters`, replacing loaded rows. */
  applyFilters: (filters: AuditLogListQuery) => Promise<void>;
  /** Load + append the next older keyset page. No-op if there is no cursor. */
  loadOlder: () => Promise<void>;
}

function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

export function useAudit(token: string): UseAuditResult {
  const [logs, setLogs] = useState<AuditLogView[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // The filters the currently-loaded rows were fetched with; `loadOlder` reuses
  // them so appended pages stay consistent with the active filter.
  const activeFiltersRef = useRef<AuditLogListQuery>({});
  // The in-flight request's controller — a newer request aborts it.
  const abortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const applyFilters = useCallback(
    async (filters: AuditLogListQuery): Promise<void> => {
      if (!token) return;

      // Supersede any in-flight request (filter change replaces everything).
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // A new filter query invalidates the previous result set: drop the rows
      // and the keyset cursor up front. This keeps the invariant "displayed rows
      // + nextCursor + activeFiltersRef always describe the same query" — a stale
      // cursor can never combine with the new filters, and `loadOlder` is a no-op
      // (its `!nextCursor` guard) until this query's own cursor arrives.
      setLogs([]);
      setNextCursor(null);
      setIsLoading(true);
      setIsLoadingMore(false);
      setError(null);

      try {
        const res = await adminApi.audit.list(
          token,
          { ...filters, limit: AUDIT_PAGE_LIMIT },
          controller.signal
        );
        // Ignore a stale/superseded result.
        if (!isMountedRef.current || abortRef.current !== controller) return;
        if (res.success) {
          // Commit the filter set only once its rows are the displayed rows, so
          // an appended `loadOlder` page always matches what is on screen.
          activeFiltersRef.current = filters;
          setLogs(res.data.logs);
          setNextCursor(res.data.nextCursor);
          setHasLoaded(true);
        } else {
          setError(res.error?.message ?? 'Failed to load the audit log.');
        }
      } catch (err) {
        if (isAbortError(err)) return; // caller-superseded — silently ignore
        if (!isMountedRef.current || abortRef.current !== controller) return;
        setError('Failed to load the audit log. Please try again.');
      } finally {
        if (isMountedRef.current && abortRef.current === controller) {
          setIsLoading(false);
        }
      }
    },
    [token]
  );

  const loadOlder = useCallback(async (): Promise<void> => {
    if (!token || !nextCursor) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoadingMore(true);
    setError(null);

    try {
      const res = await adminApi.audit.list(
        token,
        { ...activeFiltersRef.current, limit: AUDIT_PAGE_LIMIT, cursor: nextCursor },
        controller.signal
      );
      if (!isMountedRef.current || abortRef.current !== controller) return;
      if (res.success) {
        setLogs((prev) => [...prev, ...res.data.logs]);
        setNextCursor(res.data.nextCursor);
      } else {
        setError(res.error?.message ?? 'Failed to load more audit entries.');
      }
    } catch (err) {
      if (isAbortError(err)) return;
      if (!isMountedRef.current || abortRef.current !== controller) return;
      setError('Failed to load more audit entries. Please try again.');
    } finally {
      if (isMountedRef.current && abortRef.current === controller) {
        setIsLoadingMore(false);
      }
    }
  }, [token, nextCursor]);

  return {
    logs,
    hasMore: nextCursor !== null,
    isLoading,
    isLoadingMore,
    error,
    hasLoaded,
    applyFilters,
    loadOlder,
  };
}
